import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Palette, Image, X, Pencil, Settings as SettingsIcon, Plus, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PracticeConfigSection({ darkMode, practice, operatories, updateThresholds, updateAppearance, updatePracticeName, updateDisplaySettings, fetchPractice, fetchData }) {
  const [operatoryNames, setOperatoryNames] = useState([]);
  const [warningMinutes, setWarningMinutes] = useState(5);
  const [criticalMinutes, setCriticalMinutes] = useState(7);
  const [bgColor, setBgColor] = useState('#f8fafc');
  const [bgImage, setBgImage] = useState('');
  const [bgImageDark, setBgImageDark] = useState('');
  const [logoImage, setLogoImage] = useState('');
  const [buttonColors, setButtonColors] = useState({
    patient_arrived: '#0284c7', patient_seated: '#059669',
    dr_in_room: '#4f46e5', dr_needed: '#f59e0b', help_needed: '#8b5cf6'
  });
  const [practiceName, setPracticeName] = useState('');
  const [editingPracticeName, setEditingPracticeName] = useState(false);
  const [appearanceDialogOpen, setAppearanceDialogOpen] = useState(false);
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [operatoryDialogOpen, setOperatoryDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generatingDarkBg, setGeneratingDarkBg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (practice) {
      setOperatoryNames(practice.operatory_names || []);
      setWarningMinutes(Math.floor((practice.warning_threshold || 300) / 60));
      setCriticalMinutes(Math.floor((practice.critical_threshold || 420) / 60));
      setBgColor(practice.background_color || '#f8fafc');
      setBgImage(practice.background_image || '');
      setBgImageDark(practice.background_image_dark || '');
      setLogoImage(practice.logo_image || '');
      setPracticeName(practice.name || '');
      setButtonColors({
        patient_arrived: practice.button_color_patient_arrived || '#0284c7',
        patient_seated: practice.button_color_patient_seated || '#059669',
        dr_in_room: practice.button_color_dr_in_room || '#4f46e5',
        dr_needed: practice.button_color_dr_needed || '#f59e0b',
        help_needed: practice.button_color_help_needed || '#8b5cf6'
      });
    }
  }, [practice]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/practice/upload-background`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) { setBgImage(response.data.image_url); toast.success('Background image uploaded'); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload image'); }
    finally { setUploadingImage(false); }
  };

  const handleGenerateDarkBackground = async () => {
    if (!bgImage) { toast.error('Upload a light mode background first'); return; }
    setGeneratingDarkBg(true);
    try {
      const response = await axios.post(`${API}/practice/generate-dark-background`);
      if (response.data.success) { setBgImageDark(response.data.background_image_dark); toast.success('Dark mode background generated!'); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate dark background'); }
    finally { setGeneratingDarkBg(false); }
  };

  const handleSaveAppearance = async () => {
    setLoading(true);
    try {
      const result = await updateAppearance(bgColor, bgImage, bgImageDark);
      if (result.success) { toast.success('Appearance settings saved'); setAppearanceDialogOpen(false); }
      else { toast.error(result.error); }
    } catch { toast.error('Failed to save appearance settings'); }
    finally { setLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/practice/upload-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoImage(res.data.logo_url);
      toast.success('Logo uploaded successfully');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload logo'); }
    finally { setUploadingLogo(false); }
  };

  const handleRemoveLogo = async () => {
    try { await axios.delete(`${API}/practice/logo`); setLogoImage(''); toast.success('Logo removed'); }
    catch { toast.error('Failed to remove logo'); }
  };

  const handleSaveButtonColors = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/practice/button-colors`, {
        button_color_patient_arrived: buttonColors.patient_arrived,
        button_color_patient_seated: buttonColors.patient_seated,
        button_color_dr_in_room: buttonColors.dr_in_room,
        button_color_dr_needed: buttonColors.dr_needed,
        button_color_help_needed: buttonColors.help_needed
      });
      toast.success('Button colors saved');
    } catch { toast.error('Failed to save button colors'); }
    finally { setLoading(false); }
  };

  const handleUpdateOperatories = async () => {
    if (operatoryNames.length === 0) { toast.error('At least one operatory required'); return; }
    setLoading(true);
    try {
      await axios.put(`${API}/practice/operatories`, { operatory_count: operatoryNames.length, operatory_names: operatoryNames });
      toast.success('Operatories updated');
      setOperatoryDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update operatories'); }
    finally { setLoading(false); }
  };

  const handleUpdateThresholds = async () => {
    if (warningMinutes >= criticalMinutes) { toast.error('Warning threshold must be less than critical threshold'); return; }
    if (warningMinutes < 1 || criticalMinutes < 1) { toast.error('Thresholds must be at least 1 minute'); return; }
    setLoading(true);
    try {
      const result = await updateThresholds(warningMinutes * 60, criticalMinutes * 60);
      if (result.success) { toast.success('Alert thresholds updated'); setThresholdDialogOpen(false); }
      else { toast.error(result.error); }
    } catch { toast.error('Failed to update thresholds'); }
    finally { setLoading(false); }
  };

  if (!practice) return null;

  return (
    <div className={`glass-panel rounded-2xl p-6 mb-6 ${darkMode ? 'bg-slate-800/50' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Practice Configuration</h2>
        <div className="flex gap-2">
          {/* Appearance Dialog */}
          <Dialog open={appearanceDialogOpen} onOpenChange={setAppearanceDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="edit-appearance-button" variant="outline">
                <Palette className="w-4 h-4 mr-2" />Appearance
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-panel max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl">Appearance Settings</DialogTitle></DialogHeader>
              <div className="space-y-6 mt-4">
                {/* Practice Logo */}
                <div>
                  <Label className="text-base font-semibold mb-2 block">Practice Logo</Label>
                  <p className="text-sm text-slate-500 mb-3">Recommended: PNG with transparent background (max 2MB)</p>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  {logoImage ? (
                    <div className="relative bg-slate-100 rounded-lg p-4 flex items-center justify-center">
                      <img src={logoImage} alt="Practice logo" className="max-h-20 max-w-full object-contain" />
                      <Button onClick={handleRemoveLogo} size="icon" variant="destructive" className="absolute top-2 right-2 h-8 w-8"><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <Button onClick={() => logoInputRef.current?.click()} variant="outline" className="w-full h-20 border-dashed border-2" disabled={uploadingLogo}>
                      <div className="flex flex-col items-center">
                        <Image className="w-6 h-6 text-slate-400 mb-1" />
                        <span className="text-sm">{uploadingLogo ? 'Uploading...' : 'Upload Logo'}</span>
                      </div>
                    </Button>
                  )}
                </div>
                {/* Background Color */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Background Color</Label>
                  <div className="flex items-center gap-4">
                    <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} data-testid="bg-color-picker" className="w-14 h-14 rounded-lg cursor-pointer border-0 p-0 bg-transparent" />
                    <div className="flex-1">
                      <Input type="text" value={bgColor} onChange={(e) => setBgColor(e.target.value)} placeholder="#f8fafc" className="font-mono" />
                    </div>
                  </div>
                  <div className="mt-3 h-10 rounded-lg" style={{ backgroundColor: bgColor }} />
                </div>
                {/* Background Image */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Light Mode Background</Label>
                  <p className="text-sm text-slate-500 mb-3">Upload an image for light mode (max 5MB)</p>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  {bgImage ? (
                    <div className="relative">
                      <div className="h-32 rounded-lg bg-cover bg-center border border-slate-200" style={{ backgroundImage: `url(${bgImage})` }} />
                      <Button onClick={() => setBgImage('')} size="icon" variant="destructive" className="absolute top-2 right-2 h-8 w-8"><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="w-full h-24 border-dashed border-2" disabled={uploadingImage}>
                      <div className="flex flex-col items-center">
                        <Image className="w-8 h-8 text-slate-400 mb-2" />
                        <span>{uploadingImage ? 'Uploading...' : 'Click to upload'}</span>
                      </div>
                    </Button>
                  )}
                </div>
                {/* Dark Mode Background */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Dark Mode Background</Label>
                  <p className="text-sm text-slate-500 mb-3">Generate from light mode or upload separately</p>
                  {bgImageDark ? (
                    <div className="relative">
                      <div className="h-32 rounded-lg bg-cover bg-center border border-slate-700" style={{ backgroundImage: `url(${bgImageDark})`, backgroundColor: '#0f172a' }} />
                      <Button onClick={() => setBgImageDark('')} size="icon" variant="destructive" className="absolute top-2 right-2 h-8 w-8"><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <div className="h-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-900 flex items-center justify-center">
                      <span className="text-slate-500 text-sm">No dark mode background</span>
                    </div>
                  )}
                  <Button onClick={handleGenerateDarkBackground} disabled={!bgImage || generatingDarkBg} variant="outline" className="w-full mt-3">
                    {generatingDarkBg ? 'Generating...' : 'Generate Dark Mode Version'}
                  </Button>
                  {!bgImage && <p className="text-xs text-amber-600 mt-2">Upload a light mode background first to generate a dark version</p>}
                </div>
                {/* Button Colors */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Button Colors</Label>
                  <div className="space-y-3">
                    {[
                      { key: 'patient_arrived', label: 'Patient Arrived', default: '#0284c7' },
                      { key: 'patient_seated', label: 'Patient Seated', default: '#059669' },
                      { key: 'dr_in_room', label: 'Dr. in Room', default: '#4f46e5' },
                      { key: 'dr_needed', label: 'Dr. Needed', default: '#f59e0b' },
                      { key: 'help_needed', label: 'Help Needed', default: '#8b5cf6' }
                    ].map(({ key, label, default: defaultColor }) => (
                      <div key={key} className="flex items-center gap-3">
                        <input type="color" value={buttonColors[key]} onChange={(e) => setButtonColors(prev => ({ ...prev, [key]: e.target.value }))} data-testid={`button-color-${key}`} className="w-10 h-10 rounded cursor-pointer border-0 p-0 bg-transparent" />
                        <span className="flex-1 text-sm text-slate-700">{label}</span>
                        <button onClick={() => setButtonColors(prev => ({ ...prev, [key]: defaultColor }))} className="text-xs text-slate-500 hover:text-slate-700">Reset</button>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleSaveButtonColors} disabled={loading} variant="outline" className="w-full mt-3">Save Button Colors</Button>
                </div>
                <Button onClick={handleSaveAppearance} disabled={loading} className="w-full bg-dp-600 hover:bg-dp-700 text-white">
                  {loading ? 'Saving...' : 'Save Background Settings'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Alert Timing Dialog */}
          <Dialog open={thresholdDialogOpen} onOpenChange={setThresholdDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="edit-thresholds-button" variant="outline">
                <SettingsIcon className="w-4 h-4 mr-2" />Alert Timing
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-panel max-w-md">
              <DialogHeader><DialogTitle className="font-heading text-2xl">Alert Timing Settings</DialogTitle></DialogHeader>
              <div className="space-y-6 mt-4">
                <div>
                  <Label className="text-base font-semibold">Warning Threshold (Red)</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input type="number" min={1} max={60} value={warningMinutes} onChange={(e) => setWarningMinutes(parseInt(e.target.value) || 1)} className="w-24" />
                    <span className="text-sm text-slate-600">minutes</span>
                  </div>
                </div>
                <div>
                  <Label className="text-base font-semibold">Critical Threshold (Flashing)</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input type="number" min={1} max={60} value={criticalMinutes} onChange={(e) => setCriticalMinutes(parseInt(e.target.value) || 1)} className="w-24" />
                    <span className="text-sm text-slate-600">minutes</span>
                  </div>
                </div>
                <Button onClick={handleUpdateThresholds} disabled={loading} className="w-full bg-dp-600 hover:bg-dp-700 text-white">
                  {loading ? 'Saving...' : 'Save Thresholds'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Operatories Dialog */}
          <Dialog open={operatoryDialogOpen} onOpenChange={setOperatoryDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="edit-operatories-button" variant="outline">
                <SettingsIcon className="w-4 h-4 mr-2" />Edit Operatories
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-panel max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading text-2xl">Manage Operatories</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <Label>Operatories</Label>
                  <Button onClick={() => setOperatoryNames([...operatoryNames, `Operatory ${operatoryNames.length + 1}`])} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" /> Add
                  </Button>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {operatoryNames.map((name, index) => (
                    <div key={index} className="flex gap-2">
                      <Input value={name} onChange={(e) => { const u = [...operatoryNames]; u[index] = e.target.value; setOperatoryNames(u); }} />
                      {operatoryNames.length > 1 && (
                        <Button onClick={() => setOperatoryNames(operatoryNames.filter((_, i) => i !== index))} variant="outline" size="icon">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button onClick={handleUpdateOperatories} disabled={loading} className="w-full bg-dp-600 hover:bg-dp-700 text-white">
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <div className="space-y-3">
        {/* Practice Name - Editable */}
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-700">Practice:</span>
          {editingPracticeName ? (
            <div className="flex items-center gap-2 flex-1">
              <Input value={practiceName} onChange={(e) => setPracticeName(e.target.value)} className="max-w-xs" data-testid="practice-name-input" />
              <Button size="sm" onClick={async () => {
                const result = await updatePracticeName(practiceName);
                if (result.success) { toast.success('Practice name updated'); setEditingPracticeName(false); }
                else { toast.error(result.error); }
              }} className="bg-dp-600 hover:bg-dp-700 text-white">Save</Button>
              <Button size="sm" variant="outline" onClick={() => { setPracticeName(practice.name); setEditingPracticeName(false); }}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-slate-700">{practice.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setEditingPracticeName(true)} className="h-7 w-7 p-0" data-testid="edit-practice-name-btn">
                <Pencil className="w-3.5 h-3.5 text-slate-500" />
              </Button>
            </div>
          )}
        </div>
        <p className="text-slate-700"><span className="font-semibold">Operatories:</span> {practice.operatory_count}</p>
        <p className="text-slate-700">
          <span className="font-semibold">Alert Timing:</span> Red at {Math.floor((practice.warning_threshold || 300) / 60)} min, 
          Flashing at {Math.floor((practice.critical_threshold || 420) / 60)} min
        </p>
        
        {/* Show All Operatories Toggle */}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-200">
          <button
            onClick={async () => {
              const result = await updateDisplaySettings({ dashboard_show_all: !practice.dashboard_show_all });
              if (result.success) toast.success(practice.dashboard_show_all ? 'Dashboard will scroll' : 'All operatories shown');
              else toast.error(result.error);
            }}
            data-testid="toggle-show-all"
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
              practice.dashboard_show_all ? 'bg-dp-600' : 'bg-slate-300'
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
              practice.dashboard_show_all ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
          <span className="text-slate-700 text-sm">Show all operatories on dashboard</span>
          <div className="relative inline-block ml-1">
            <button
              onMouseEnter={(e) => { e.currentTarget.nextSibling.style.display = 'block'; }}
              onMouseLeave={(e) => { e.currentTarget.nextSibling.style.display = 'none'; }}
              className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
              }`}
              data-testid="show-all-ops-info"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            <div style={{ display: 'none' }} className={`absolute z-50 w-72 p-3 text-xs rounded-lg shadow-xl bottom-6 left-0 ${
              darkMode ? 'bg-slate-700 text-slate-200 border border-slate-600' : 'bg-white text-slate-700 border border-slate-200'
            }`}>
              When enabled, every user sees all operatories on the dashboard regardless of their provider assignment. When disabled, users only see operatories assigned to them.
            </div>
          </div>
        </div>

        {/* Nightly Provider Reset Toggle */}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-200">
          <button
            onClick={async () => {
              const newVal = !practice.nightly_provider_reset;
              try {
                await axios.put(`${API}/practice/settings`, { nightly_provider_reset: newVal });
                fetchPractice();
                toast.success(newVal ? 'Provider labels will reset nightly' : 'Provider labels will persist daily');
              } catch { toast.error('Failed to update setting'); }
            }}
            data-testid="toggle-nightly-provider-reset"
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
              practice.nightly_provider_reset ? 'bg-dp-600' : (darkMode ? 'bg-slate-600' : 'bg-slate-300')
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
              practice.nightly_provider_reset ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Reset provider display on dashboard nightly</span>
          <div className="relative inline-block ml-1">
            <button
              onMouseEnter={(e) => { e.currentTarget.nextSibling.style.display = 'block'; }}
              onMouseLeave={(e) => { e.currentTarget.nextSibling.style.display = 'none'; }}
              className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
              }`}
              data-testid="nightly-reset-info"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            <div style={{ display: 'none' }} className={`absolute z-50 w-72 p-3 text-xs rounded-lg shadow-xl bottom-6 left-0 ${
              darkMode ? 'bg-slate-700 text-slate-200 border border-slate-600' : 'bg-white text-slate-700 border border-slate-200'
            }`}>
              When enabled, provider labels are cleared from all operatory boxes on the dashboard at midnight. Operatories will display by number only until providers are reassigned for the new day.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
